// Import new configuration types
import type {
  ClearConfigurationCacheResponse,
  CompanyConfigurationHistoryResource,
  CompanyConfigurationResource,
  ConfigurationDefinitionResource,
  CreateSystemConfigurationRequest,
  SystemConfigurationResource,
  UpdateCompanyConfigurationRequest,
  UpdateSystemConfigurationRequest,
} from '@interfaces/system/configuration.types';
import { createSlice } from '@reduxjs/toolkit';
import type { AppDispatch, AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

export interface ConfigurationReducerState {
  // System configurations (super admin)
  systemConfigurations: Record<string, SystemConfigurationResource>;
  systemConfigurationDefinitions: Record<string, ConfigurationDefinitionResource>;
  systemConfigurationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  systemConfigurationError: string | null;

  // Company configurations
  companyConfigurations: Record<string, CompanyConfigurationResource>;
  companyConfigurationHistory: Record<string, CompanyConfigurationHistoryResource[]>;
  companyConfigurationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  companyConfigurationError: string | null;

  // Cache management
  lastCacheClear: string | null;
}

const initialState: ConfigurationReducerState = {
  systemConfigurations: {},
  systemConfigurationDefinitions: {},
  systemConfigurationStatus: 'idle',
  systemConfigurationError: null,
  companyConfigurations: {},
  companyConfigurationHistory: {},
  companyConfigurationStatus: 'idle',
  companyConfigurationError: null,
  lastCacheClear: null,
};

// System Configuration Thunks (Super Admin)
export const getSystemConfigurations = thunkGet.get<undefined>('configuration', '/api/v1/admin/configurations');

export const getSystemConfigurationsByCategory = thunkGet.get<{ category: string }>(
  'configuration',
  '/api/v1/admin/configurations/category/{category}',
);

export const getSystemConfiguration = thunkGet.get<{ key: string }>(
  'configuration',
  '/api/v1/admin/configurations/{key}',
);

export const createSystemConfiguration = thunkPost.post<CreateSystemConfigurationRequest>(
  'configuration',
  '/api/v1/admin/configurations',
);

export const updateSystemConfiguration = thunkPut.put<UpdateSystemConfigurationRequest, { key: string }>(
  'configuration',
  '/api/v1/admin/configurations/{key}',
);

export const clearConfigurationCache = thunkPost.post('configuration', '/api/v1/admin/configurations/cache/clear');

// Company Configuration Thunks
export const getCompanyConfigurations = thunkGet.compUrl<undefined>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations',
);

export const getCompanyConfigurationsByCategory = thunkGet.compUrl<{ category: string }>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations/category/{category}',
);

export const getCompanyConfiguration = thunkGet.compUrl<{ key: string }>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations/{key}',
);

export const getCompanyConfigurationHistory = thunkGet.compUrl<{ key: string }>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations/{key}/history',
);

export const updateCompanyConfiguration = thunkPut.compUrl<UpdateCompanyConfigurationRequest, { key: string }>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations/{key}',
);

export const resetCompanyConfiguration = thunkDelete.compUrl<{ key: string }>(
  'configuration',
  '/api/v1/c/{companySlug}/admin/configurations/{key}',
);

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    clearConfigurationState: (state) => {
      state.systemConfigurations = {};
      state.systemConfigurationDefinitions = {};
      state.companyConfigurations = {};
      state.companyConfigurationHistory = {};
      state.systemConfigurationStatus = 'idle';
      state.companyConfigurationStatus = 'idle';
      state.systemConfigurationError = null;
      state.companyConfigurationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Logout action
      .addCase(meLogOut.fulfilled, (state) => {
        state.systemConfigurations = {};
        state.systemConfigurationDefinitions = {};
        state.companyConfigurations = {};
        state.companyConfigurationHistory = {};
        state.systemConfigurationStatus = 'idle';
        state.companyConfigurationStatus = 'idle';
        state.systemConfigurationError = null;
        state.companyConfigurationError = null;
      })

      // System Configurations
      .addCase(getSystemConfigurations.pending, (state) => {
        state.systemConfigurationStatus = 'loading';
        state.systemConfigurationError = null;
      })
      .addCase(getSystemConfigurations.fulfilled, (state, action) => {
        state.systemConfigurationStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((config: SystemConfigurationResource) => {
            state.systemConfigurations[config.key] = config;
          });
        }
      })
      .addCase(getSystemConfigurations.rejected, (state, action) => {
        state.systemConfigurationStatus = 'failed';
        state.systemConfigurationError = action.error.message || 'Failed to load system configurations';
      })

      // System Configuration by Category
      .addCase(getSystemConfigurationsByCategory.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((config: SystemConfigurationResource) => {
            state.systemConfigurations[config.key] = config;
          });
        }
      })

      // Single System Configuration
      .addCase(getSystemConfiguration.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.systemConfigurations[action.payload.key] = action.payload;
        }
      })

      // Create System Configuration
      .addCase(createSystemConfiguration.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.systemConfigurations[action.payload.key] = action.payload;
        }
      })

      // Update System Configuration
      .addCase(updateSystemConfiguration.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.systemConfigurations[action.payload.key] = action.payload;
        }
      })

      // Clear Configuration Cache
      .addCase(clearConfigurationCache.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === 'object' && 'clearedAt' in action.payload) {
          state.lastCacheClear = (action.payload as ClearConfigurationCacheResponse).clearedAt;
        }
      })

      // Company Configurations
      .addCase(getCompanyConfigurations.pending, (state) => {
        state.companyConfigurationStatus = 'loading';
        state.companyConfigurationError = null;
      })
      .addCase(getCompanyConfigurations.fulfilled, (state, action) => {
        state.companyConfigurationStatus = 'succeeded';
        if (Array.isArray(action.payload)) {
          action.payload.forEach((config: CompanyConfigurationResource) => {
            state.companyConfigurations[config.key] = config;
          });
        }
      })
      .addCase(getCompanyConfigurations.rejected, (state, action) => {
        state.companyConfigurationStatus = 'failed';
        state.companyConfigurationError = action.error.message || 'Failed to load company configurations';
      })

      // Company Configuration by Category
      .addCase(getCompanyConfigurationsByCategory.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          action.payload.forEach((config: CompanyConfigurationResource) => {
            state.companyConfigurations[config.key] = config;
          });
        }
      })

      // Single Company Configuration
      .addCase(getCompanyConfiguration.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.companyConfigurations[action.payload.key] = action.payload;
        }
      })

      // Company Configuration History
      .addCase(getCompanyConfigurationHistory.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap?.key) {
          const { key } = action.meta.arg.urlVarsReplaceMap;
          if (Array.isArray(action.payload)) {
            state.companyConfigurationHistory[key] = action.payload;
          }
        }
      })

      // Update Company Configuration
      .addCase(updateCompanyConfiguration.fulfilled, (state, action) => {
        if (action.payload?.key) {
          state.companyConfigurations[action.payload.key] = action.payload;
        }
      })

      // Reset Company Configuration
      .addCase(resetCompanyConfiguration.fulfilled, (state, action) => {
        if (action.meta.arg?.urlVarsReplaceMap?.key) {
          const { key } = action.meta.arg.urlVarsReplaceMap;
          // Remove the company-specific override, will inherit from system default
          delete state.companyConfigurations[key];
        }
      });
  },
});

export const { clearConfigurationState } = configurationSlice.actions;

// Selectors
export const selectSystemConfigurations = (state: AppState) => state.configuration.systemConfigurations;
export const selectSystemConfigurationStatus = (state: AppState) => state.configuration.systemConfigurationStatus;
export const selectSystemConfigurationError = (state: AppState) => state.configuration.systemConfigurationError;

export const selectCompanyConfigurations = (state: AppState) => state.configuration.companyConfigurations;
export const selectCompanyConfigurationStatus = (state: AppState) => state.configuration.companyConfigurationStatus;
export const selectCompanyConfigurationError = (state: AppState) => state.configuration.companyConfigurationError;

export const selectCompanyConfigurationHistory = (state: AppState) => state.configuration.companyConfigurationHistory;
export const selectLastCacheClear = (state: AppState) => state.configuration.lastCacheClear;

// Helper selectors
export const selectSystemConfigurationByKey = (key: string) => (state: AppState) =>
  state.configuration.systemConfigurations[key];

export const selectCompanyConfigurationByKey = (key: string) => (state: AppState) =>
  state.configuration.companyConfigurations[key];

export const selectConfigurationHistoryByKey = (key: string) => (state: AppState) =>
  state.configuration.companyConfigurationHistory[key] || [];

// Load initial data action

export const loadInitialConfigurationData = () => (_dispatch: AppDispatch) => {
  // Load system configurations if user has super admin role
  // This should be called conditionally based on user permissions
};

export default configurationSlice.reducer;
